import { useEffect, useState, useCallback, useMemo } from 'react';
import { useApi } from '../../../utils/hooks';
import { SortDirection } from '../../../utils/constants/enums';
import {
  IAnnualInvoiceDto,
  IAttritionReportResult,
  IPagedResult
} from '../../../utils/constants/interfaces';
import { ReportColumn, SearchCriterion } from '../../../components/ui/ReportActions';
import { useReportFormatters } from '../../../utils/hooks/useReportFormatters';
import { getCurrentBillingYear } from '../../../utils/helpers/billingYear';

const emptyPagedResults: IPagedResult<IAnnualInvoiceDto[]> = {
  currentPage: 1,
  results: [],
  pageSize: 1,
  pageCount: 1,
  resultCount: 0
};

interface AttritionSearchParameters {
  page: number;
  pageSize: number;
  sortField: string;
  sortDirection: SortDirection;
}

const useAttritionReport = () => {
  const formatters = useReportFormatters();
  const api = useApi();

  const currentBillingYear = getCurrentBillingYear();

  // Year choices for the billing-year picker: current billing year back to the
  // earliest year with data (2014), newest first.
  const billingYearOptions = useMemo(() => {
    const earliestYear = 2014;
    const years: number[] = [];
    for (let year = currentBillingYear; year >= earliestYear; year--) {
      years.push(year);
    }
    return years;
  }, [currentBillingYear]);

  const [loading, setLoading] = useState<boolean>(true);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  // billingYear is the value actually applied to the query; pendingBillingYear
  // holds what the user is typing in the input until they commit it.
  const [billingYear, setBillingYear] = useState<number>(currentBillingYear);
  const [pendingBillingYear, setPendingBillingYear] = useState<string>(
    currentBillingYear.toString()
  );
  const [totalInvoicesForBillingYear, setTotalInvoicesForBillingYear] = useState<number>(0);
  const [allResults, setAllResults] = useState<IAnnualInvoiceDto[]>([]);
  const [pagedResults, setPagedResults] =
    useState<IPagedResult<IAnnualInvoiceDto[]>>(emptyPagedResults);
  const [searchParameters, setSearchParameters] = useState<AttritionSearchParameters>({
    page: 1,
    pageSize: 50,
    sortField: 'lastName',
    sortDirection: SortDirection.ASCENDING
  });

  const applyClientSidePaginationAndSort = useCallback(() => {
    if (allResults.length === 0) {
      setPagedResults(emptyPagedResults);
      return;
    }

    // Group households so a secondary adult always sits with its primary: sort by
    // the PRIMARY member's value for the active column, then keep the two rows
    // adjacent (primary ahead of secondary) within each household.
    const primaryByHousehold = new Map<number, IAnnualInvoiceDto>();
    for (const row of allResults) {
      if (!row.isSecondaryAdult) {
        primaryByHousehold.set(row.clubMemberId, row);
      }
    }

    const getGroupSortValue = (
      row: IAnnualInvoiceDto
    ): string | number | boolean | Date | null => {
      const primary = primaryByHousehold.get(row.clubMemberId) ?? row;
      const value = primary[
        searchParameters.sortField as keyof IAnnualInvoiceDto
      ] as string | number | boolean | Date | null;
      return typeof value === 'string' ? value.toLowerCase() : value;
    };

    const sortedResults = [...allResults].sort((a, b) => {
      const direction: SortDirection = searchParameters.sortDirection;
      const aValue = getGroupSortValue(a);
      const bValue = getGroupSortValue(b);

      let comparison = 0;
      if (aValue === null && bValue === null) comparison = 0;
      else if (aValue === null) comparison = direction === SortDirection.ASCENDING ? -1 : 1;
      else if (bValue === null) comparison = direction === SortDirection.ASCENDING ? 1 : -1;
      else if (aValue < bValue) comparison = direction === SortDirection.ASCENDING ? -1 : 1;
      else if (aValue > bValue) comparison = direction === SortDirection.ASCENDING ? 1 : -1;
      if (comparison !== 0) return comparison;

      // Same sort value → keep households grouped (stable by id), primary first.
      if (a.clubMemberId !== b.clubMemberId) return a.clubMemberId - b.clubMemberId;
      return Number(a.isSecondaryAdult) - Number(b.isSecondaryAdult);
    });

    const total = sortedResults.length;
    const pageCount = Math.max(1, Math.ceil(total / searchParameters.pageSize));
    const startIndex = (searchParameters.page - 1) * searchParameters.pageSize;
    const endIndex = startIndex + searchParameters.pageSize;
    const pageResults = sortedResults.slice(startIndex, endIndex);

    setPagedResults({
      currentPage: searchParameters.page,
      results: pageResults,
      pageSize: searchParameters.pageSize,
      pageCount: pageCount,
      resultCount: total
    });
  }, [
    allResults,
    searchParameters.sortField,
    searchParameters.sortDirection,
    searchParameters.page,
    searchParameters.pageSize
  ]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const apiResponse = await api.get<IAttritionReportResult>(
        `/members/reports/attrition-report?billingYear=${billingYear}`
      );
      if (apiResponse?.status?.statusCode === 200 && apiResponse.response) {
        setAllResults(apiResponse.response.invoices ?? []);
        setTotalInvoicesForBillingYear(apiResponse.response.totalInvoicesForBillingYear ?? 0);
      } else {
        setAllResults([]);
        setTotalInvoicesForBillingYear(0);
      }
    } catch {
      setAllResults([]);
      setTotalInvoicesForBillingYear(0);
    } finally {
      setHasLoaded(true);
      setLoading(false);
    }
  }, [api, billingYear]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const applyBillingYear = (yearValue?: string) => {
    const parsed = parseInt(yearValue ?? pendingBillingYear, 10);
    if (!isNaN(parsed) && parsed > 1900 && parsed < 3000 && parsed !== billingYear) {
      setBillingYear(parsed);
      setSearchParameters((prev) => ({ ...prev, page: 1 }));
    }
  };

  const resetBillingYear = () => {
    setPendingBillingYear(currentBillingYear.toString());
    if (billingYear !== currentBillingYear) {
      setBillingYear(currentBillingYear);
      setSearchParameters((prev) => ({ ...prev, page: 1 }));
    }
  };

  useEffect(() => {
    applyClientSidePaginationAndSort();
  }, [applyClientSidePaginationAndSort]);

  const changePage = (page: number) => {
    if (page === searchParameters.page) return;
    setSearchParameters({ ...searchParameters, page });
  };

  const changeSort = (sortField: string) => {
    const currentDirection: SortDirection = searchParameters.sortDirection;
    const sortDirection =
      searchParameters.sortField === sortField
        ? currentDirection === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING
        : SortDirection.ASCENDING;
    setSearchParameters({
      ...searchParameters,
      sortField,
      sortDirection,
      page: 1
    });
  };

  const getReportColumns = (): ReportColumn<IAnnualInvoiceDto>[] => [
    { key: 'isSecondaryAdult', header: 'Type', formatter: (value) => (value ? 'Secondary' : 'Primary') },
    { key: 'memberId', header: 'Member_ID' },
    { key: 'lastName', header: 'Last_Name' },
    { key: 'firstName', header: 'First_Name' },
    { key: 'memberStatus', header: 'Member_Status' },
    { key: 'memberTypeName', header: 'Member_Type' },
    { key: 'phone', header: 'Phone' },
    { key: 'cellPhone', header: 'Cell_Phone' },
    { key: 'email', header: 'Email' },
    // Invoice money belongs to the household and is emitted once, on the primary
    // row. Secondary rows leave these cells empty so summing a column in the
    // exported file matches the report's totals instead of double-counting the
    // shared household invoice.
    {
      key: 'amountPaidForBillingYear',
      header: 'Amount_Paid',
      formatter: (value, row) =>
        row.isSecondaryAdult ? '' : formatters.formatCurrency(value as number | null)
    },
    {
      key: 'balanceDue',
      header: 'Balance_Due',
      formatter: (value, row) =>
        row.isSecondaryAdult ? '' : formatters.formatCurrency(value as number | null)
    },
    {
      key: 'lastPaymentDateUtc',
      header: 'Last_Payment_Date',
      formatter: (value, row) =>
        row.isSecondaryAdult ? '' : formatters.formatDateFromUtc(value as Date | null)
    },
    { key: 'billingYear', header: 'Billing_Year' }
  ];

  const getSortedResultsForExport = (): IAnnualInvoiceDto[] => {
    const primaryByHousehold = new Map<number, IAnnualInvoiceDto>();
    for (const row of allResults) {
      if (!row.isSecondaryAdult) {
        primaryByHousehold.set(row.clubMemberId, row);
      }
    }
    const primaryOf = (row: IAnnualInvoiceDto) =>
      primaryByHousehold.get(row.clubMemberId) ?? row;

    // Match the on-screen grouping: order households by the primary's name, then
    // keep the secondary adult directly beneath its primary.
    return [...allResults].sort((a, b) => {
      const pa = primaryOf(a);
      const pb = primaryOf(b);
      const lastNameCompare = (pa.lastName || '')
        .toLowerCase()
        .localeCompare((pb.lastName || '').toLowerCase());
      if (lastNameCompare !== 0) return lastNameCompare;
      const firstNameCompare = (pa.firstName || '')
        .toLowerCase()
        .localeCompare((pb.firstName || '').toLowerCase());
      if (firstNameCompare !== 0) return firstNameCompare;
      if (a.clubMemberId !== b.clubMemberId) return a.clubMemberId - b.clubMemberId;
      return Number(a.isSecondaryAdult) - Number(b.isSecondaryAdult);
    });
  };

  const getSearchCriteria = (): SearchCriterion[] => [
    { label: 'Billing Year', value: billingYear.toString() }
  ];

  // Financial totals and invoice count should only consider primary rows,
  // since primary + secondary adult share one invoice.
  const primaryRows = useMemo(
    () => allResults.filter((row) => !row.isSecondaryAdult),
    [allResults]
  );

  const totalBalanceDue = primaryRows.reduce((sum, row) => sum + row.balanceDue, 0);
  const totalPaid = primaryRows.reduce((sum, row) => sum + row.amountPaidForBillingYear, 0);
  // Non-Renewal = distinct memberships (households) with a non-renewal event: the
  // lapsed-primary households (one primary row each, status INACTIVE/RESIGNED/EXPELLED,
  // incl. $0 Life/Honorary) plus households where only the secondary adult left (primary
  // still active, so the household has a secondary row but no primary row). Counting
  // distinct clubMemberId folds a fully-lapsed household's primary + secondary rows into
  // one membership, so a household is never double-counted. This drives the attrition %,
  // keeping the numerator and the household-based denominator in the same unit.
  const lostMembers = new Set(allResults.map((row) => row.clubMemberId)).size;
  // Of those, how many still owe money — a lapsed $0 Life/Honorary member is "lost"
  // but carries no outstanding balance.
  const invoicesWithBalance = primaryRows.filter((row) => row.balanceDue > 0).length;

  const attritionPercent =
    totalInvoicesForBillingYear > 0
      ? (lostMembers / totalInvoicesForBillingYear) * 100
      : 0;

  return {
    loading,
    hasLoaded,
    billingYear,
    billingYearOptions,
    pendingBillingYear,
    setPendingBillingYear,
    applyBillingYear,
    resetBillingYear,
    totalInvoicesForBillingYear,
    searchParameters,
    pagedResults,
    allResults,
    lostMembers,
    invoicesWithBalance,
    attritionPercent,
    changePage,
    changeSort,
    reload: loadReport,
    getReportColumns,
    getSortedResultsForExport,
    getSearchCriteria,
    totalBalanceDue,
    totalPaid
  };
};

export default useAttritionReport;
